import { CategoryDisplay } from '../config/categoryConfig'

const iconPaths: Record<string, string> = {
  'book-open':
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  'academic-cap':
    'M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5',
  'play-circle':
    'M21 12a9 9 0 11-18 0 9 9 0 0118 0z M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z',
}

const colorClasses: Record<string, { bg: string; text: string; border: string; progressBg: string }> = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', progressBg: 'bg-emerald-500' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', progressBg: 'bg-indigo-500' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', progressBg: 'bg-purple-500' },
}

interface CategoryStats {
  totalCards: number
  masteredCards: number
  exerciseCount: number
}

interface StudyAreaCardProps {
  category: CategoryDisplay
  stats: CategoryStats | null
  onClick: () => void
  loading?: boolean
}

export function StudyAreaCard({ category, stats, onClick, loading = false }: StudyAreaCardProps) {
  const colors = colorClasses[category.color] || colorClasses.emerald
  const iconPath = iconPaths[category.icon] || iconPaths['book-open']

  const progressPercent =
    stats && stats.totalCards > 0 ? Math.round((stats.masteredCards / stats.totalCards) * 100) : 0

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-6 rounded-xl border-2 ${colors.border} ${colors.bg} hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
    >
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-lg bg-white shadow-sm">
          <svg
            className={`w-8 h-8 ${colors.text}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
          <p className="text-sm text-gray-600 mt-1">{category.description}</p>

          {loading ? (
            <div className="mt-4 h-2 bg-gray-200 rounded-full animate-pulse" />
          ) : stats ? (
            <div className="mt-4">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${colors.progressBg} transition-all duration-300`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex justify-between items-center mt-2 text-sm text-gray-600">
                <span>{stats.masteredCards} / {stats.totalCards} mastered</span>
                <span>{progressPercent}%</span>
              </div>
              {stats.exerciseCount > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                  {stats.exerciseCount} exercise{stats.exerciseCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 text-sm text-gray-500">No cards yet</div>
          )}
        </div>

        <svg
          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  )
}
